import axios, {
    type AxiosError,
    AxiosHeaders,
    HttpStatusCode,
    type InternalAxiosRequestConfig,
} from 'axios';

import { getDeviceInfo, Axios as moniebookAxios } from '@moniebook-web/utils';

import { GLOBAL_CONFIG } from './config';
import { getTokenVariable } from './cookies';
import { logger } from './log';
import { csrfTokenManager } from './services/csrf-service';
import { useBusinessStore, useStaffSessionStore } from './store';

const CSRF_ERROR_CODES = new Set([
    'csrf_missing_token',
    'invalid_client',
    'invalid_csrf_token',
]);

declare module 'axios' {
    interface AxiosRequestConfig {
        ignoreAuthToken?: boolean;
    }
}

declare module 'axios' {
    interface AxiosRequestConfig {
        ignoreAuthToken?: boolean;
    }
}

const Axios = axios.create({
    baseURL: GLOBAL_CONFIG.MONIEBOOK_SERVICE_URL,
});

Axios.defaults.headers.post['Content-Type'] = 'application/json';
Axios.defaults.headers.post.Accept = '*/*';
Axios.defaults.timeout = 30_000;

const requestInterceptor = async (
    requestConfig: InternalAxiosRequestConfig,
) => {
    const { deviceUniqueIdentifier, manufacturer } = getDeviceInfo();

    if (!requestConfig.headers) {
        requestConfig.headers = new AxiosHeaders();
    }

    const csrf = csrfTokenManager.getToken();
    if (csrf) {
        requestConfig.headers['x-mp-csrf'] = csrf;
    }

    requestConfig.headers['device-id'] = deviceUniqueIdentifier;
    requestConfig.headers['client-type'] = manufacturer;
    requestConfig.headers['client-version'] = '0.0.0';
    requestConfig.headers['X-Device-Timestamp'] = new Date().toISOString();
    requestConfig.headers['X-Device-Type'] = 'web-register';
    requestConfig.headers['X-Timezone'] =
        Intl.DateTimeFormat().resolvedOptions().timeZone;

    const businessId = useBusinessStore.getState().businessInfo?.businessId;

    if (businessId) {
        requestConfig.headers['business-id'] = businessId;
    }

    const token = getTokenVariable();

    if (
        token &&
        !requestConfig.ignoreAuthToken &&
        !requestConfig.headers.Authorization
    ) {
        requestConfig.headers['Authorization'] = `Bearer ${token}`;
    }

    const { staffId, staffSignature, userId } = useStaffSessionStore.getState();

    if (staffSignature) {
        requestConfig.headers['x-staff-signature'] = staffSignature;
    }

    if (staffId) {
        requestConfig.headers['business-user-id'] = staffId;
    }

    if (userId) {
        requestConfig.headers['user-id'] = userId;
    }

    return requestConfig;
};

type CsrfRetryConfig = InternalAxiosRequestConfig & {
    __csrfRetried?: boolean;
};

const SAFE_TO_RETRY_METHODS = new Set(['get']);

function isCsrfFailure(error: AxiosError): boolean {
    if (error.response?.status !== HttpStatusCode.Forbidden) {
        return false;
    }

    const data = error.response.data as
        | { error?: { code?: string } }
        | undefined;
    const code = data?.error?.code;

    return typeof code === 'string' && CSRF_ERROR_CODES.has(code);
}

const responseInterceptor = async (error: AxiosError) => {
    const statusCode = error?.response?.status;

    if (statusCode === HttpStatusCode.Unauthorized) {
        if (error.config?.ignoreAuthToken) {
            return Promise.reject(error);
        }

        console.error('[401] Unauthorized', {
            url: error.config?.url,
            method: error.config?.method,
            response: error.response?.data,
            page: globalThis.location.href,
        });
    }

    if (isCsrfFailure(error)) {
        const config = error.config as CsrfRetryConfig | undefined;
        const method = config?.method?.toLowerCase();

        logger.warn({
            eventName: 'csrf.recovery.triggered',
            message: `url=${config?.url} method=${method}`,
        });

        if (!config || config.__csrfRetried) {
            logger.error({
                eventName: 'csrf.recovery.retry.failure',
                message: `url=${config?.url} method=${method} reason=${
                    config ? 'already_retried' : 'no_config'
                }`,
            });
            return Promise.reject(error);
        }

        try {
            await csrfTokenManager.refresh();
        } catch {
            return Promise.reject(error);
        }

        if (!method || !SAFE_TO_RETRY_METHODS.has(method)) {
            return Promise.reject(error);
        }

        config.__csrfRetried = true;

        try {
            const retryResponse = await Axios(config);
            logger.info({
                eventName: 'csrf.recovery.retry.success',
                message: `url=${config.url} method=${method}`,
            });
            return retryResponse;
        } catch (retryError) {
            logger.error({
                eventName: 'csrf.recovery.retry.failure',
                message: `url=${config.url} method=${method} reason=retry_failed`,
            });
            return Promise.reject(retryError);
        }
    }

    return Promise.reject(error);
};

moniebookAxios.defaults.baseURL = GLOBAL_CONFIG.MONIEBOOK_SERVICE_URL;
moniebookAxios.interceptors.request.clear();

moniebookAxios.interceptors.request.use(requestInterceptor);
moniebookAxios.interceptors.response.use(
    (response) => response,
    responseInterceptor,
);
Axios.interceptors.request.use(requestInterceptor);
Axios.interceptors.response.use((response) => response, responseInterceptor);

export { Axios };
